import React, {
  useState, useEffect, useMemo, useCallback, useRef,
} from 'react';
import moment from 'moment';
import {
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
  TrophyIcon,
  UserGroupIcon,
  CpuChipIcon,
  SparklesIcon,
} from '@heroicons/react-v2/24/outline';

import {cx} from '../../util';
import {getPracticeAnalytics} from '../../api/api';
import {
  Gauge,
  AttributeBars,
  LevelGroupedBars,
  TrendLine,
  ScorePill,
  scoreBand,
  fmtScore,
  pct,
} from './charts';

const SOURCE_ORDER = ['peer', 'aiPeer', 'aiSolo'];
const SOURCE_ICONS = {peer: UserGroupIcon, aiPeer: SparklesIcon, aiSolo: CpuChipIcon};

const PRESETS = [
  {key: '30', label: 'Last 30 days', days: 30},
  {key: '90', label: 'Last 90 days', days: 90},
  {key: '365', label: 'Last 12 months', days: 365},
  {key: 'all', label: 'All time', days: null},
];

const toISODate = (m) => m.format('YYYY-MM-DD');

const rangeForPreset = (preset) => {
  const endDate = toISODate(moment());
  if (preset === 'all') return {startDate: '2019-01-01', endDate};
  const days = PRESETS.find((p) => p.key === preset)?.days || 90;
  return {startDate: toISODate(moment().subtract(days, 'days')), endDate};
};

const Stat = ({label, value}) => (
  <div>
    <div className="text-xl font-bold tabular-nums text-gray-900">{value}</div>
    <div className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</div>
  </div>
);

const Card = ({className, children}) => (
  <div className={cx('rounded-2xl border border-gray-200 bg-white p-6 shadow-sm', className)}>{children}</div>
);

const SectionTitle = ({icon: Icon, title, subtitle}) => (
  <div className="mb-4 flex items-start gap-3">
    {Icon && (
      <div className="mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
        <Icon className="h-5 w-5" />
      </div>
    )}
    <div>
      <h2 className="text-lg font-bold text-gray-900">{title}</h2>
      {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
    </div>
  </div>
);

const PracticeAnalyticsPage = ({initialReport = null, initialSourceKey = 'peer'}) => {
  const [report, setReport] = useState(initialReport);
  const [loading, setLoading] = useState(!initialReport);
  const [error, setError] = useState(null);
  const [preset, setPreset] = useState('90');
  const [range, setRange] = useState(rangeForPreset('90'));
  const [sourceKey, setSourceKey] = useState(initialSourceKey);
  const [role, setRole] = useState('all');
  const skipNextFetch = useRef(Boolean(initialReport));

  const fetchReport = useCallback(async (r, selectedRole) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPracticeAnalytics({
        ...r,
        role: selectedRole === 'all' ? undefined : selectedRole,
      });
      setReport(data);
    } catch (err) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    fetchReport(range, role);
  }, [range, role, fetchReport]);

  const onPreset = (key) => {
    setPreset(key);
    if (key !== 'custom') setRange(rangeForPreset(key));
  };

  const onCustomDate = (field, value) => {
    setPreset('custom');
    setRange((prev) => ({...prev, [field]: value}));
  };

  const source = report?.sources?.[sourceKey];
  const availableRoles = report?.availableRoles || [];

  const struggles = useMemo(() => (source?.types || [])
    .filter((t) => t.weakestAttribute)
    .map((t) => ({type: t.type, name: t.name, ...t.weakestAttribute}))
    .sort((a, b) => a.avg - b.avg), [source]);

  // Pivot seniority breakdown into per-attribute grouped rows (peer only).
  const levelRows = useMemo(() => {
    const levels = source?.levels || [];
    if (levels.length < 2) return [];
    const map = {};
    levels.forEach((lvl) => lvl.attributes.forEach((a) => {
      if (a.count === 0) return;
      if (!map[a.key]) map[a.key] = {key: a.key, label: a.label, byLevel: {}};
      map[a.key].byLevel[lvl.level] = {avg: a.avg, count: a.count};
    }));
    return Object.values(map);
  }, [source]);

  const questions = useMemo(() => (source?.questions || []).slice(0, 10), [source]);
  const roleSuffix = role === 'all' ? '' : ` · ${availableRoles.find((r) => r.slug === role)?.label || role}`;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900">Interview Insights</h1>
        <p className="max-w-3xl text-gray-500">
          Rubric scores across peer mock interviews and AI practice, broken down by interview type,
          attribute, role, and seniority. All scores are on a 1–5 scale.
        </p>
      </div>

      {/* Date filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => onPreset(p.key)}
              className={cx(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                preset === p.key ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-50',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <input
            type="date"
            value={range.startDate}
            max={range.endDate}
            onChange={(e) => onCustomDate('startDate', e.target.value)}
            className="rounded-lg border border-gray-200 px-2 py-1.5 text-gray-700 shadow-sm"
          />
          <span>→</span>
          <input
            type="date"
            value={range.endDate}
            min={range.startDate}
            onChange={(e) => onCustomDate('endDate', e.target.value)}
            className="rounded-lg border border-gray-200 px-2 py-1.5 text-gray-700 shadow-sm"
          />
        </div>
        {availableRoles.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-500">Role</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 shadow-sm"
            >
              <option value="all">All roles</option>
              {availableRoles.map((r) => (
                <option key={r.slug} value={r.slug}>{`${r.label} (${r.count})`}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-8 rounded-xl border border-rose-200 bg-rose-50 p-4 text-rose-700">{error}</div>
      )}

      {loading && !report && (
        <div className="mt-16 flex animate-pulse flex-col items-center text-gray-400">
          <div className="text-lg font-semibold">Crunching rubric scores…</div>
        </div>
      )}

      {report && (
        <>
          {/* Summary cards per feedback source */}
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {SOURCE_ORDER.map((key) => {
              const s = report.sources[key];
              const Icon = SOURCE_ICONS[key];
              const active = key === sourceKey;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSourceKey(key)}
                  className={cx(
                    'rounded-2xl border bg-white p-5 text-left shadow-sm transition-all',
                    active ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-gray-200 hover:border-gray-300',
                  )}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="text-sm font-semibold text-gray-900">{s.label}</div>
                    </div>
                    <Gauge avg={s.overall.avg} size={88} />
                  </div>
                  <div className="mt-4 flex items-end justify-between">
                    <Stat label="Sessions" value={s.sessions.toLocaleString()} />
                    <Stat label="Evaluations" value={s.evaluations.toLocaleString()} />
                    <Stat label="Avg score" value={fmtScore(s.overall.avg)} />
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex items-center gap-2 text-sm text-gray-500">
            <span className="font-semibold text-gray-700">{source?.label}</span>
            {roleSuffix && <span>{roleSuffix}</span>}
          </div>

          {!source || source.evaluations === 0 ? (
            <Card className="mt-3 text-center text-gray-500">
              {source?.roleUnavailable && role !== 'all'
                ? 'Role breakdown isn’t available for AI peer feedback (speakers can’t be mapped to a specific candidate).'
                : `No ${source?.label} feedback for this selection.`}
            </Card>
          ) : (
            <>
              {/* Struggle leaderboard + trend */}
              <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Card>
                  <SectionTitle
                    icon={ExclamationTriangleIcon}
                    title="Where candidates struggle most"
                    subtitle="Lowest-scoring rubric attribute for each interview type"
                  />
                  <div className="flex flex-col divide-y divide-gray-100">
                    {struggles.map((s) => (
                      <div key={s.type} className="flex items-center justify-between py-2.5">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{s.label}</div>
                          <div className="text-xs text-gray-500">{s.name}</div>
                        </div>
                        <ScorePill avg={s.avg} />
                      </div>
                    ))}
                    {struggles.length === 0 && <div className="py-4 text-sm text-gray-400">No data.</div>}
                  </div>
                </Card>

                <Card>
                  <SectionTitle
                    icon={ArrowTrendingUpIcon}
                    title="Score trend over time"
                    subtitle="Monthly average rubric score across all attributes"
                  />
                  <TrendLine trend={source.trend} />
                </Card>
              </div>

              {/* Per interview type breakdown */}
              <div className="mt-8">
                <SectionTitle
                  title="Rubric breakdown by interview type"
                  subtitle="Average score per attribute — the core of what candidates do well or poorly"
                />
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {source.types.map((t) => {
                    const band = scoreBand(t.overall.avg);
                    return (
                      <Card key={t.type}>
                        <div className="mb-4 flex items-start justify-between">
                          <div>
                            <h3 className="text-base font-bold text-gray-900">{t.name}</h3>
                            <div className="mt-1 text-xs text-gray-500">
                              {`${t.evaluations.toLocaleString()} evaluations`}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={cx('rounded-full px-2 py-0.5 text-xs font-semibold', band.bg, band.text)}>{band.label}</span>
                            <Gauge avg={t.overall.avg} size={64} />
                          </div>
                        </div>
                        <AttributeBars attributes={t.attributes} />
                        <div className="mt-4 flex flex-wrap gap-2 text-xs">
                          {t.strongestAttribute && (
                            <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 font-medium text-emerald-700">
                              <TrophyIcon className="h-3.5 w-3.5" />
                              {`Best: ${t.strongestAttribute.label} (${fmtScore(t.strongestAttribute.avg)})`}
                            </span>
                          )}
                          {t.weakestAttribute && (
                            <span className="inline-flex items-center gap-1 rounded-md bg-rose-50 px-2 py-1 font-medium text-rose-700">
                              <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                              {`Weakest: ${t.weakestAttribute.label} (${fmtScore(t.weakestAttribute.avg)})`}
                            </span>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>

              {/* Seniority comparison (peer only) */}
              {levelRows.length > 0 && (
                <Card className="mt-8">
                  <SectionTitle
                    icon={UserGroupIcon}
                    title="Junior vs. senior candidates"
                    subtitle="Average rubric score by self-reported experience level (peer interviews)"
                  />
                  <div className="grid grid-cols-1 gap-x-10 gap-y-6 md:grid-cols-2">
                    <LevelGroupedBars
                      rows={levelRows.slice(0, Math.ceil(levelRows.length / 2))}
                      levels={source.levels}
                    />
                    <LevelGroupedBars
                      rows={levelRows.slice(Math.ceil(levelRows.length / 2))}
                      levels={source.levels}
                    />
                  </div>
                </Card>
              )}

              {/* Hardest questions */}
              {questions.length > 0 && (
                <Card className="mt-8">
                  <SectionTitle
                    icon={ExclamationTriangleIcon}
                    title="Hardest questions for interviewees"
                    subtitle="Questions with the lowest average rubric scores"
                  />
                  <div className="flex flex-col divide-y divide-gray-100">
                    {questions.map((q) => (
                      <div key={`${q.type}-${q.question}`} className="grid grid-cols-[1fr_120px_56px] items-center gap-4 py-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-gray-800" title={q.question}>{q.question}</div>
                          <div className="text-xs text-gray-400">
                            {`${q.typeName} · ${q.evaluations} eval${q.evaluations === 1 ? '' : 's'}`}
                          </div>
                        </div>
                        <div className="h-2.5 overflow-hidden rounded-full bg-gray-100">
                          <div className={cx('h-full rounded-full', scoreBand(q.avg).solid)} style={{width: `${pct(q.avg)}%`}} />
                        </div>
                        <div className="text-right"><ScorePill avg={q.avg} /></div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default PracticeAnalyticsPage;
